import type { JSX } from 'react';
import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Group,
  Paper,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { browserSupportsWebAuthn, startAuthentication } from '@simplewebauthn/browser';
import { ApiError, api } from '../api';
import { StandalonePage } from '../ui/PageScroll';

export function LoginPage(): JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const queryClient = useQueryClient();
  const health = useQuery({ queryKey: ['health'], queryFn: api.health, staleTime: 60_000 });

  const login = useMutation({
    mutationFn: () => api.login({ email, password, remember }),
    onSuccess: (user) => queryClient.setQueryData(['me'], user),
  });

  // Passkey: the device offers its passkeys for this domain; no address typed.
  const passkey = useMutation({
    mutationFn: async () => {
      const optionsJSON = await api.passkeyLoginOptions();
      const response = await startAuthentication({ optionsJSON });
      return api.passkeyLogin(response, remember);
    },
    onSuccess: (user) => queryClient.setQueryData(['me'], user),
  });
  const supportsPasskeys = browserSupportsWebAuthn();

  const throttled = login.error instanceof ApiError && login.error.status === 429;
  const passkeyError = passkey.error
    ? passkey.error instanceof ApiError
      ? passkey.error.status === 429 ? 'Zu viele Versuche. Bitte in einigen Minuten erneut versuchen.' : passkey.error.message
      : 'Abgebrochen oder auf diesem Gerät kein Passkey für PST.'
    : null;

  return (
    <StandalonePage>
      <Paper withBorder shadow="sm" p="xl" w={380} maw="100%">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            login.mutate();
          }}
        >
          <Stack>
            <Group gap="sm" align="center">
              <Title order={2}>PST</Title>
              {health.data?.instance && <Badge color="orange" variant="filled">{health.data.instance}</Badge>}
            </Group>

            {login.isError && (
              <Alert color="red" title="Anmeldung fehlgeschlagen">
                {throttled
                  ? 'Zu viele Versuche. Bitte in einigen Minuten erneut versuchen.'
                  : 'E-Mail-Adresse oder Passwort falsch.'}
              </Alert>
            )}

            <TextInput
              label="E-Mail-Adresse"
              type="email"
              autoComplete="username"
              required
              data-autofocus
              value={email}
              onChange={(event) => setEmail(event.currentTarget.value)}
            />
            <PasswordInput
              label="Passwort"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.currentTarget.value)}
            />
            <Checkbox
              label="Angemeldet bleiben"
              description="30 Tage, nur auf eigenen Geräten"
              checked={remember}
              onChange={(event) => setRemember(event.currentTarget.checked)}
            />
            <Button type="submit" loading={login.isPending} fullWidth>
              Anmelden
            </Button>
            {supportsPasskeys && (
              <>
                <Button variant="default" fullWidth loading={passkey.isPending} onClick={() => { login.reset(); passkey.mutate(); }}>
                  Mit Passkey anmelden
                </Button>
                {passkeyError && <Alert color="red" py={6}>{passkeyError}</Alert>}
              </>
            )}
            <Text size="xs" c="dimmed" ta="center">
              Ein verlorenes Gerät lässt sich über „Auf allen Geräten abmelden" im
              Menü oben rechts sofort aussperren.
            </Text>
          </Stack>
        </form>
      </Paper>
    </StandalonePage>
  );
}
